import { Formik } from 'formik';
import { useContext, useEffect, useState } from 'react';

import { CustomerEditContext } from './CustomerEdit';

import { CloseButton } from '@components/ActionButtons';
import { Box } from '@components/Box';
import {
  FormSectionContainer,
  FormButtonsContainer,
} from '@components/Container';
import {
  FormikInput,
  CancelButton,
  SubmitButton,
  FormikCheckbox,
} from '@components/Form';
import { ActionableSelect } from '@components/Form/ActionableSelect';
import { OkButton } from '@components/Form/OkButton';
import { Loading } from '@components/Loading';
import { Modal } from '@components/Modal';
import { FormikSelectOption } from '@models/common/FormikSelectOption';
import {
  ContactViewModel,
  contactDefaultValue,
  ContactValidation,
} from '@models/Contact';
import { ContactService } from '@services/Contact';
import { CustomerService } from '@services/Customer';

export const CustomerEditContactSelect = () => {
  const { customer, setCustomer } = useContext(CustomerEditContext);
  const [isCustomerContactsLoading, setIsCustomerContactsLoading] =
    useState(true);
  const customerContacts = customer.contacts || [];
  const [customerContactSelectOptions, setCustomerContactSelectOptions] =
    useState<FormikSelectOption[]>([]);
  const [isShowAddContact, setIsShowAddContact] = useState(false);
  const [isShowEditContact, setIsShowEditContact] = useState(false);
  const [isShowDeleteContact, setShowDeleteContact] = useState(false);
  const [contactToEdit, setContactToEdit] = useState<ContactViewModel | null>();
  const [isLoading, setIsLoading] = useState(true);

  const loadCustomerContacts = () => {
    CustomerService.getCustomerContacts(Number(customer.id)).then((result) => {
      if (result.isSuccess && 'data' in result) {
        setCustomer((prevState) => ({
          ...prevState,
          contacts: result.data,
        }));
      }

      setIsCustomerContactsLoading(false);
    });
  };

  const loadContactOptions = () => {
    const customerContactSelection = [{ value: '0', text: '' }].concat(
      customerContacts.map((row) => ({
        value: `${row.id}`,
        text: `${row.firstname} ${row.lastname}`,
      }))
    );

    setCustomerContactSelectOptions(customerContactSelection);
  };

  useEffect(() => {
    loadContactOptions();
  }, [customer.contacts]);

  useEffect(() => {
    if (!isCustomerContactsLoading) {
      setIsLoading(false);
    }
  }, [isCustomerContactsLoading]);

  useEffect(() => {
    loadCustomerContacts();
  }, [customer.id]);

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <>
        <ActionableSelect
          label="Contact"
          value={`${customer.contactID}`}
          selection={customerContactSelectOptions}
          onAddAction={() => {
            setIsShowAddContact(true);
          }}
          onEditAction={() => {
            if (customer.contactID == undefined || customer.contactID == 0)
              return;

            const selectedContact = customerContacts.find(
              (row) => row.id == customer.contactID
            );

            if (selectedContact == undefined) return;

            setContactToEdit(selectedContact);
            setIsShowEditContact(true);
          }}
          onDeleteAction={() => setShowDeleteContact(true)}
          onDuplicateAction={() => {
            const selectedContact = customerContacts.find(
              (row) => row.id == customer.contactID
            );

            if (selectedContact == undefined) return;

            selectedContact.id = 0;
            ContactService.create(selectedContact).then((result) => {
              if (result.isSuccess && 'data' in result) {
                const newContact = {
                  ...selectedContact,
                  id: result.data.id,
                };

                const updatedContacts = customerContacts.concat(newContact);

                setCustomer({
                  ...customer,
                  contacts: updatedContacts,
                });

                setContactToEdit(newContact);
                setIsShowEditContact(true);
              }
            });
          }}
          onChange={(e) => {
            const updateCustomer = {
              ...customer,
              contactID: Number(e.target.value),
            };
            setCustomer(updateCustomer);
          }}
        />

        {isShowDeleteContact && (
          <>
            <Modal show={true}>
              <CloseButton onClick={() => setShowDeleteContact(false)} />
              <Box size="medium">
                <>
                  <div className="px-4 pb-2">
                    <span>
                      Are you sure you want to delete this record from your
                      contact lists?
                    </span>
                  </div>
                  <FormButtonsContainer>
                    <div className="space-x-2">
                      <CancelButton
                        onClick={() => setShowDeleteContact(false)}
                      />
                      <OkButton
                        onClick={async () => {
                          const deleteContact = customerContacts.find(
                            (row) => row.id == customer.contactID
                          );

                          if (deleteContact == undefined) return;

                          const result = await ContactService.delete(
                            deleteContact.id
                          );
                          if (result.isSuccess) {
                            const updatedContacts = customerContacts.filter(
                              (row) => row.id !== customer.contactID
                            );

                            const nextContactId =
                              updatedContacts.length > 0
                                ? updatedContacts[0].id
                                : 0;

                            setCustomer({
                              ...customer,
                              contactID: nextContactId,
                              contacts: updatedContacts,
                            });
                            setShowDeleteContact(false);
                          } else {
                            console.error(
                              'Error: Failed to delete the record.',
                              result.errorMessages
                            );
                            alert(`Error: ${result.errorMessages}`);
                          }
                        }}
                      >
                        Delete
                      </OkButton>
                    </div>
                  </FormButtonsContainer>
                </>
              </Box>
            </Modal>
          </>
        )}

        {isShowAddContact && (
          <>
            <Modal show={true}>
              <CloseButton onClick={() => setIsShowAddContact(false)} />

              <Formik
                initialValues={contactDefaultValue}
                validationSchema={ContactValidation}
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={(values, actions) => {
                  values.customerId = Number(customer.id);

                  ContactService.create(values).then((result) => {
                    if (result.isSuccess && 'data' in result) {
                      values.id = result.data.id;
                      const updatedContacts = customerContacts.concat(values);

                      let activeId = values.id;

                      if (!values.isActive) {
                        const firstActive = updatedContacts.find(
                          (item) => item.isActive
                        );
                        activeId = firstActive ? firstActive.id : 0;
                      }

                      setCustomer({
                        ...customer,
                        contactID: activeId,
                        contacts: updatedContacts,
                      });

                      actions.resetForm();
                      setIsShowAddContact(false);
                    }
                  });
                }}
              >
                {(formikProps) => {
                  return (
                    <>
                      <form method="POST" onSubmit={formikProps.handleSubmit}>
                        <FormSectionContainer>
                          <FormikInput label="Firstname" name="firstname" />
                          <FormikInput label="Lastname" name="lastname" />
                          <FormikInput label="Email" name="email" />
                          <FormikInput label="Phone" name="phone" />
                          <FormikInput label="Fax" name="fax" />
                          <FormikCheckbox label="Is Active" name="isActive" />
                        </FormSectionContainer>
                        <FormButtonsContainer>
                          <CancelButton
                            onClick={() => {
                              setIsShowAddContact(false);
                              formikProps.resetForm();
                            }}
                          />

                          <SubmitButton
                            label="Save"
                            disabled={
                              formikProps.isSubmitting || !formikProps.isValid
                            }
                          />
                        </FormButtonsContainer>
                      </form>
                    </>
                  );
                }}
              </Formik>
            </Modal>
          </>
        )}

        {isShowEditContact && contactToEdit && (
          <>
            <Modal show={true}>
              <CloseButton onClick={() => setIsShowEditContact(false)} />

              <Formik
                initialValues={contactToEdit}
                validationSchema={ContactValidation(contactToEdit.id)}
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={(values, actions) => {
                  ContactService.update(values.id, values).then((result) => {
                    if (result.isSuccess) {
                      const updatedContacts = customerContacts.map((row) =>
                        row.id === values.id ? values : row
                      );

                      let activeId = values.id;

                      if (!values.isActive) {
                        const firstActive = updatedContacts.find(
                          (item) => item.isActive
                        );
                        activeId = firstActive ? firstActive.id : 0;
                      }

                      setCustomer({
                        ...customer,
                        contactID: activeId,
                        contacts: updatedContacts,
                      });

                      actions.resetForm();
                      setIsShowEditContact(false);
                    } else {
                      console.error(
                        'Error: Failed to update record.',
                        result.errorMessages
                      );
                      alert(`Error: ${result.errorMessages}`);
                    }
                  });
                }}
              >
                {(formikProps) => {
                  return (
                    <>
                      <form method="POST" onSubmit={formikProps.handleSubmit}>
                        <FormSectionContainer>
                          <FormikInput label="Firstname" name="firstname" />
                          <FormikInput label="Lastname" name="lastname" />
                          <FormikInput label="Email" name="email" />
                          <FormikInput label="Phone" name="phone" />
                          <FormikInput label="Fax" name="fax" />
                          <FormikCheckbox label="Is Active" name="isActive" />
                        </FormSectionContainer>
                        <FormButtonsContainer>
                          <CancelButton
                            onClick={() => {
                              setIsShowEditContact(false);
                              formikProps.resetForm();
                            }}
                          />

                          <SubmitButton
                            label="Save"
                            disabled={
                              formikProps.isSubmitting || !formikProps.isValid
                            }
                          />
                        </FormButtonsContainer>
                      </form>
                    </>
                  );
                }}
              </Formik>
            </Modal>
          </>
        )}
      </>
    );
  }
};
